import React, { useState, useEffect } from "react";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Loader2, ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from "lucide-react";
import useModalStore from "../../stores/modalStore";
import useUserStore from "../../stores/userStore";
import { hasPermission } from "../../utils/permissions";
import { fetchEsstReport } from "../../services/payrollService";
import useSettingsStore from "../../stores/settingsStore";

const EsstReportModal = () => {
    const show = useModalStore(state => state.showEsstReportModal);
    const modalProps = useModalStore(state => state.modalProps?.esstReportModal);
    const closeModal = useModalStore(state => state.closeModal);

    const employees = useUserStore(state => state.employees);
    const piiClass = useSettingsStore(s => s.blurPII) ? 'blur-pii' : '';
    const activeEmployees = employees.filter(e => e.end_date == null);

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [currentUserId, setCurrentUserId] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const initialUserId = modalProps?.userId;

    useEffect(() => {
        if (show && initialUserId) {
            setCurrentUserId(initialUserId);
            loadReport(initialUserId, year);
        }
        if (!show) {
            setReportData(null);
            setError(null);
            setYear(new Date().getFullYear());
            setCurrentUserId(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, initialUserId]);

    const loadReport = async (uid, yr) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchEsstReport(uid, yr);
            setReportData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (newYear) => {
        setYear(newYear);
        if (currentUserId) loadReport(currentUserId, newYear);
    };

    const currentIndex = activeEmployees.findIndex(e => e.user_id === currentUserId);

    const handlePrevEmployee = () => {
        if (currentIndex > 0) {
            const prev = activeEmployees[currentIndex - 1];
            setCurrentUserId(prev.user_id);
            loadReport(prev.user_id, year);
        }
    };

    const handleNextEmployee = () => {
        if (currentIndex < activeEmployees.length - 1) {
            const next = activeEmployees[currentIndex + 1];
            setCurrentUserId(next.user_id);
            loadReport(next.user_id, year);
        }
    };

    const currentEmployee = activeEmployees[currentIndex];

    if (!show) return null;

    const summary = reportData?.summary;
    const days = reportData?.days || [];

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`bg-white shadow-xl flex flex-col overflow-hidden transition-all duration-300 ${isFullscreen ? "w-full h-full rounded-none" : "rounded-2xl w-full max-w-6xl max-h-[95vh]"}`}>
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        {hasPermission('view_team_status') && (
                            <button
                                onClick={handlePrevEmployee}
                                disabled={loading || currentIndex <= 0}
                                className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-500 hover:!text-gray-700 disabled:!opacity-30 disabled:!cursor-not-allowed !transition-colors"
                                title="Previous employee"
                            >
                                <ChevronLeft size={20} />
                            </button>
                        )}
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                ESST Report — <span className={piiClass}>{reportData?.employee_name || currentEmployee?.first_name || `User ${currentUserId}`}</span>
                            </h2>
                            <p className="text-sm text-gray-500">
                                Accrual rate: 1 hr per 30 hrs worked · Max 48/yr · Bank max 80
                            </p>
                        </div>
                        {hasPermission('view_team_status') && (
                            <button
                                onClick={handleNextEmployee}
                                disabled={loading || currentIndex >= activeEmployees.length - 1}
                                className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-500 hover:!text-gray-700 disabled:!opacity-30 disabled:!cursor-not-allowed !transition-colors"
                                title="Next employee"
                            >
                                <ChevronRight size={20} />
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-1">
                            <button
                                onClick={() => handleYearChange(year - 1)}
                                className="p-1 hover:bg-gray-100 rounded"
                                disabled={loading}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="px-2 text-sm font-semibold">{year}</span>
                            <button
                                onClick={() => handleYearChange(year + 1)}
                                className="p-1 hover:bg-gray-100 rounded"
                                disabled={loading || year >= new Date().getFullYear()}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                        <button
                            onClick={() => setIsFullscreen(f => !f)}
                            className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                        >
                            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                        <button
                            onClick={() => closeModal("esstReportModal")}
                            className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 p-6 space-y-6">
                    {loading && (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="animate-spin text-blue-600" size={32} />
                            <span className="ml-3 text-gray-500">Loading ESST data...</span>
                        </div>
                    )}

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {!loading && !error && reportData && (
                        <>
                            {/* Summary Stats */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                <StatCard label="Days Worked" value={summary.days_worked} />
                                <StatCard label="Total Hours" value={summary.total_hours} />
                                <StatCard label="ESST Accrued YTD" value={`${summary.esst_accrued_ytd} hrs`} color="text-blue-600" />
                                <StatCard label="ESST Bank" value={`${summary.esst_bank} hrs`} color="text-green-600" />
                                <StatCard label="Sick Used (Protected)" value={`${summary.total_sick_used} hrs`} color="text-red-500" />
                                <StatCard label="Sick (Unprotected)" value={`${summary.total_unprotected} hrs`} color="text-orange-500" />
                                <StatCard label="Total Sick Used" value={`${(parseFloat(summary.total_sick_used) + parseFloat(summary.total_unprotected)).toFixed(2)} hrs`} color="text-rose-700" />
                            </div>

                            {/* Cumulative Chart */}
                            {days.length > 0 && (
                                <div className="bg-white border rounded-lg p-4">
                                    <h3 className="text-sm font-semibold mb-3">Cumulative Hours & ESST Over Time</h3>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={days}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 11 }}
                                                tickFormatter={(d) => {
                                                    const dt = new Date(d + "T00:00:00");
                                                    return `${dt.getMonth() + 1}/${dt.getDate()}`;
                                                }}
                                            />
                                            <YAxis
                                                yAxisId="left"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: "Cumulative Hours", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: "ESST Hours", angle: 90, position: "insideRight", style: { fontSize: 11 } }}
                                            />
                                            <Tooltip
                                                labelFormatter={(d) => d}
                                                contentStyle={{ fontSize: 12 }}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="cumulative_hours"
                                                name="Cumulative Hours"
                                                stroke="#6366f1"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="cumulative_esst_accrued"
                                                name="ESST Accrued"
                                                stroke="#2563eb"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="esst_bank"
                                                name="ESST Bank"
                                                stroke="#16a34a"
                                                strokeWidth={2}
                                                strokeDasharray="5 5"
                                                dot={false}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey={(d) => d.cumulative_sick_used + d.cumulative_unprotected}
                                                name="Cumulative Sick"
                                                stroke="#dc2626"
                                                strokeWidth={1.5}
                                                strokeDasharray="3 3"
                                                dot={false}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Daily Bar Chart */}
                            {days.length > 0 && (
                                <div className="bg-white border rounded-lg p-4">
                                    <h3 className="text-sm font-semibold mb-3">Daily Hours Worked / Sick</h3>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart data={days}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 10 }}
                                                tickFormatter={(d) => {
                                                    const dt = new Date(d + "T00:00:00");
                                                    return `${dt.getMonth() + 1}/${dt.getDate()}`;
                                                }}
                                            />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip
                                                labelFormatter={(d) => d}
                                                contentStyle={{ fontSize: 12 }}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar dataKey="hours_worked" name="Hours Worked" fill="#818cf8" stackId="daily" />
                                            <Bar dataKey="sick_hours_today" name="Sick Hours" fill="#fca5a5" stackId="daily" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Data Table */}
                            {days.length > 0 && (
                                <div className="bg-white border rounded-lg overflow-hidden">
                                    <h3 className="text-sm font-semibold p-4 pb-2">Daily Detail</h3>
                                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                                        <table className="w-full text-xs border-collapse">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="px-2 py-2 text-left border-b">#</th>
                                                    <th className="px-2 py-2 text-left border-b">Date</th>
                                                    <th className="px-2 py-2 text-left border-b">Type</th>
                                                    <th className="px-2 py-2 text-right border-b">Scheduled</th>
                                                    <th className="px-2 py-2 text-right border-b">Worked</th>
                                                    <th className="px-2 py-2 text-right border-b">Cum. Hours</th>
                                                    <th className="px-2 py-2 text-right border-b">ESST Today</th>
                                                    <th className="px-2 py-2 text-right border-b">Cum. ESST</th>
                                                    <th className="px-2 py-2 text-right border-b">Sick Hrs</th>
                                                    <th className="px-2 py-2 text-right border-b">Unprotected</th>
                                                    <th className="px-2 py-2 text-right border-b">ESST Bank</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {days.map((d, i) => (
                                                    <tr
                                                        key={d.date}
                                                        className={d.is_sick ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"}
                                                    >
                                                        <td className="px-2 py-1.5 border-b text-gray-500">{d.day_number}</td>
                                                        <td className="px-2 py-1.5 border-b font-mono">{d.date}</td>
                                                        <td className="px-2 py-1.5 border-b">
                                                            {d.is_sick ? (
                                                                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-semibold">
                                                                    Sick
                                                                </span>
                                                            ) : (
                                                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold">
                                                                    Work
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-1.5 border-b text-right">{d.hours_scheduled}</td>
                                                        <td className="px-2 py-1.5 border-b text-right">{d.hours_worked}</td>
                                                        <td className="px-2 py-1.5 border-b text-right">{d.cumulative_hours}</td>
                                                        <td className="px-2 py-1.5 border-b text-right">{d.esst_accrued_today > 0 ? d.esst_accrued_today : ""}</td>
                                                        <td className="px-2 py-1.5 border-b text-right">{d.cumulative_esst_accrued}</td>
                                                        <td className="px-2 py-1.5 border-b text-right text-red-600 font-semibold">
                                                            {d.sick_hours_today > 0 ? d.sick_hours_today : ""}
                                                        </td>
                                                        <td className="px-2 py-1.5 border-b text-right text-orange-600 font-semibold">
                                                            {d.unprotected_today > 0 ? d.unprotected_today : ""}
                                                        </td>
                                                        <td className="px-2 py-1.5 border-b text-right font-semibold text-green-700">
                                                            {d.esst_bank}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {days.length === 0 && (
                                <div className="text-center py-10 text-gray-400">
                                    No timesheet data found for {year}.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, color = "text-gray-900" }) => (
    <div className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
        <div className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">{label}</div>
        <div className={`text-lg font-bold mt-0.5 ${color}`}>{value}</div>
    </div>
);

export default EsstReportModal;
